import apiClient from './api';
import { LoginRequest, UserDTO } from '../types/api';

/**
 * Authentication service for user login/logout operations.
 * Uses session-based authentication with cookies.
 */

/** Supported social-login providers (matches backend URL slugs). */
export type OAuthProviderSlug = 'vk' | 'yandex' | 'mailru';

/**
 * Backend URL that kicks off the OAuth2 flow for a provider.
 * The browser must navigate to it (full-page redirect), not fetch it via axios.
 */
export const oauthAuthorizeUrl = (provider: OAuthProviderSlug): string =>
  `/api/auth/oauth/${provider}/authorize`;

/**
 * Complete a social login when the provider returned no email.
 * @param email Email supplied by the user
 * @returns User data (session established server-side)
 */
export const completeOAuth = async (email: string): Promise<UserDTO> => {
  const response = await apiClient.post<UserDTO>('/api/auth/oauth/complete', { email });
  return response.data;
};

/**
 * Login with username and password.
 * @param credentials Login credentials
 * @returns User data (session-based auth, no token needed)
 */
export const login = async (credentials: LoginRequest): Promise<UserDTO> => {
  const response = await apiClient.post<UserDTO>('/api/auth/login', credentials);
  return response.data;
};

/**
 * Logout the current user.
 * Invalidates the session and clears cookies.
 */
export const logout = async (): Promise<void> => {
  await apiClient.post('/api/auth/logout');
};

/**
 * Get the currently authenticated user's information.
 * @returns Current user data or null if not authenticated
 */
export const getCurrentUser = async (): Promise<UserDTO | null> => {
  try {
    const response = await apiClient.get<UserDTO>('/api/auth/me');
    return response.data;
  } catch (error) {
    return null;
  }
};

/**
 * Check if the user is authenticated.
 * @returns True if authenticated, false otherwise
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const user = await getCurrentUser();
  return user !== null;
};
